'use client';

/**
 * PanelSection Component
 *
 * Collapsible content section for organizing SplitPanel content.
 */

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { alpha, Box, Collapse, IconButton, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useState } from 'react';
import { useReducedMotion } from '../../hooks';

// ============================================================================
// Types
// ============================================================================

export interface PanelSectionProps {
  /** Section title */
  title: string;
  /** Optional icon to display before title */
  icon?: React.ReactNode;
  /** Initially expanded state */
  defaultExpanded?: boolean;
  /** Controlled expanded state */
  expanded?: boolean;
  /** Callback when expanded state changes */
  onExpandedChange?: (expanded: boolean) => void;
  /** Additional actions to display in section header */
  actions?: React.ReactNode;
  /** Section content */
  children: React.ReactNode;
  /** Disable collapse functionality */
  collapsible?: boolean;
  /** Show divider below section */
  divider?: boolean;
}

// ============================================================================
// Styled Components
// ============================================================================

const SectionContainer = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{ $hasDivider?: boolean }>(({ theme, $hasDivider }) => ({
  marginBottom: theme.spacing(1.5),
  paddingBottom: $hasDivider ? theme.spacing(1.5) : 0,
  borderBottom: $hasDivider ? `1px solid ${theme.palette.divider}` : 'none',

  '&:last-child': {
    marginBottom: 0,
    paddingBottom: 0,
    borderBottom: 'none',
  },
}));

const SectionHeader = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{ $collapsible?: boolean }>(({ theme, $collapsible }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(0.75, 0),
  cursor: $collapsible ? 'pointer' : 'default',
  borderRadius: 6,
  marginLeft: theme.spacing(-1),
  marginRight: theme.spacing(-1),
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
  transition: 'background-color 150ms ease',

  ...($collapsible && {
    '&:hover': {
      backgroundColor:
        theme.palette.mode === 'dark'
          ? alpha(theme.palette.common.white, 0.05)
          : alpha(theme.palette.common.black, 0.03),
    },
  }),
}));

const TitleSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const TitleIcon = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.text.secondary,

  '& > svg': {
    fontSize: '1.125rem',
  },
}));

const ActionsSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
}));

interface ExpandButtonProps {
  $expanded: boolean;
}

const ExpandButton = styled(IconButton, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<ExpandButtonProps>(({ theme, $expanded }) => ({
  padding: 4,
  color: theme.palette.text.secondary,
  transform: $expanded ? 'rotate(180deg)' : 'rotate(0deg)',
  transition: 'transform 200ms ease',

  '&:hover': {
    color: theme.palette.text.primary,
    backgroundColor:
      theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.1) : alpha(theme.palette.common.black, 0.06),
  },
}));

const SectionContent = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(0.75),
}));

// ============================================================================
// Component
// ============================================================================

export const PanelSection: React.FC<PanelSectionProps> = ({
  title,
  icon,
  defaultExpanded = true,
  expanded: controlledExpanded,
  onExpandedChange,
  actions,
  children,
  collapsible = true,
  divider = false,
}) => {
  const reducedMotion = useReducedMotion();

  // Controlled vs uncontrolled state
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  const isExpanded = controlledExpanded !== undefined ? controlledExpanded : internalExpanded;

  const handleToggle = () => {
    if (!collapsible) return;

    const newExpanded = !isExpanded;
    setInternalExpanded(newExpanded);
    onExpandedChange?.(newExpanded);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!collapsible) return;

    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleToggle();
    }
  };

  return (
    <SectionContainer $hasDivider={divider}>
      <SectionHeader
        data-split-panel-section-header="true"
        $collapsible={collapsible}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        role={collapsible ? 'button' : undefined}
        tabIndex={collapsible ? 0 : undefined}
        aria-expanded={collapsible ? isExpanded : undefined}
      >
        <TitleSection>
          {icon && <TitleIcon>{icon}</TitleIcon>}
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              fontSize: '0.8125rem',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: 'text.secondary',
            }}
          >
            {title}
          </Typography>
        </TitleSection>

        <ActionsSection>
          {actions}
          {collapsible && (
            <ExpandButton
              $expanded={isExpanded}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleToggle();
              }}
              aria-label={isExpanded ? 'Collapse section' : 'Expand section'}
            >
              <ExpandMoreIcon fontSize="small" />
            </ExpandButton>
          )}
        </ActionsSection>
      </SectionHeader>

      {reducedMotion ? (
        isExpanded && <SectionContent>{children}</SectionContent>
      ) : (
        <Collapse in={isExpanded} timeout={200}>
          <SectionContent>{children}</SectionContent>
        </Collapse>
      )}
    </SectionContainer>
  );
};

export default PanelSection;
